/**
 * The rules for the reason attached to every state change (S0.5 / DA-3339).
 *
 * Pure functions, deliberately apart from `ConfirmDialog`: the same rule has
 * to hold in the dialog that collects the reason and anywhere else one is
 * entered, and a rule embedded in a component can only be tested by rendering
 * one.
 *
 * CLIENT-SIDE REJECTION IS AN AFFORDANCE, NOT ENFORCEMENT. The API must apply
 * the same rule — see S0.6 / DA-3340, where server-side authorization and
 * validation live. Today nothing downstream checks it, which is a reason to
 * keep this honest rather than to lean on it.
 */

/**
 * The shortest reason that can tell a reader anything.
 *
 * The audit log already had a free-text `source` field, and it degenerated
 * into values nobody could interpret — which is the failure this whole story
 * exists to prevent. A non-empty check does not prevent it: "x" passes and
 * says nothing. Ten characters is the PRD's number.
 */
export const REASON_MIN = 10;

/**
 * Why this reason cannot be submitted, or null when it can.
 *
 * Returns a SENTENCE rather than a boolean, and names how many characters are
 * still needed: an operator told only "too short" retypes blindly, which is
 * how a 10-character floor produces ten characters of nonsense.
 */
export function reasonProblem(reason: string): string | null {
  const n = reason.trim().length;
  if (n === 0) return "A reason is required — it goes onto the audit record.";
  if (n < REASON_MIN) {
    const need = REASON_MIN - n;
    return `${need} more character${need === 1 ? "" : "s"} — a reason has to mean something to someone who was not here.`;
  }
  return null;
}

/**
 * Jira keys in this instance: uppercase project, hyphen, digits.
 *
 * Anchored on both sides so a key glued to a longer token does not match.
 * `covid-19` is lowercase and must not linkify; `ADA-12` would turn part of
 * an unrelated word into a link to an issue that does not exist. Both were
 * real enough to be worth a test apiece.
 */
const TICKET = /\b([A-Z][A-Z0-9]+-\d+)\b/g;

/** One run of text, carrying a ticket key when that run IS one. */
export interface TicketSpan {
  text: string;
  key: string | null;
}

/**
 * Split a reason into plain runs and ticket references, in order.
 *
 * Returns SPANS rather than HTML so the caller renders them — building markup
 * here would mean trusting this module to escape an operator's free text, and
 * the reason field is exactly the place someone pastes an angle bracket.
 *
 * The spans always reassemble into the input. A test asserts it, because a
 * tokenizer that drops a character silently rewrites what someone wrote.
 */
export function ticketSpans(reason: string): TicketSpan[] {
  const spans: TicketSpan[] = [];
  let last = 0;

  // `matchAll` rather than a stateful `exec` loop: TICKET is a module-level
  // regex with /g, and `lastIndex` would persist between calls.
  for (const m of reason.matchAll(TICKET)) {
    const at = m.index ?? 0;
    if (at > last) spans.push({ text: reason.slice(last, at), key: null });
    spans.push({ text: m[0], key: m[1]! });
    last = at + m[0].length;
  }

  if (last < reason.length) spans.push({ text: reason.slice(last), key: null });
  // An empty input yields no spans at all, which would render nothing; one
  // empty plain span keeps "render every span" a total rule for the caller.
  return spans.length > 0 ? spans : [{ text: reason, key: null }];
}

/** Where a ticket key lives. One place, so a moved instance is one edit. */
export function ticketHref(key: string): string {
  return `https://foodics-products.atlassian.net/browse/${encodeURIComponent(key)}`;
}
